import { useEffect, useRef, useState, useCallback } from 'react';
import { logger } from '../utils/logger';

/**
 * useResource — small read-only data fetching primitive.
 *
 *  const { data, loading, error, refresh } = useResource(
 *    ['commitments', statusFilter],
 *    async ({ signal }) => (await api.get('/commitments', { signal, params: { status: statusFilter } })).data,
 *  );
 *
 * Features:
 *   - Cancels the in-flight request when the component unmounts OR when `key` changes.
 *   - Skips setState after unmount to silence "Can't perform a React state update…" warnings.
 *   - `refresh()` re-runs with a fresh AbortController.
 *   - Cross-component invalidation via `invalidate(key)` — every mounted subscriber with
 *     a matching key (string OR array whose first segment matches) immediately re-runs.
 *   - Pre-populated cache via `primeResource(key, data)` — lets a parent fetch a
 *     super-bundle once and feed multiple child useResource consumers without each
 *     widget firing its own network call.
 *
 * Not a React-Query replacement — intentionally tiny. Adopt only where
 * plain useEffect+fetch currently lives.
 */

// ── Module-level priming cache ──────────────────────────────────────────────
// Maps a stable key signature → { data, expiresAt }. Callers (typically a
// top-level page like TodayView) can pre-populate this map after fetching a
// super-bundle, and useResource consumers will read from cache on first
// render instead of firing their own request. Each entry has a short TTL
// (15 s) so stale data doesn't linger if a user navigates away and back.
const PRIME_TTL_MS = 15_000;
const primedCache = new Map();

function sigOf(key) {
  return Array.isArray(key) || (typeof key === 'object' && key !== null)
    ? JSON.stringify(key)
    : String(key);
}

/**
 * Seed the cache for a useResource key with pre-fetched data. The next
 * `useResource(key, …)` call that mounts within `PRIME_TTL_MS` will use
 * this data without firing its own network request.
 */
export function primeResource(key, data) {
  const sig = sigOf(key);
  primedCache.set(sig, { data, expiresAt: Date.now() + PRIME_TTL_MS });
}

function consumePrime(keySig) {
  const entry = primedCache.get(keySig);
  if (!entry) return null;
  if (entry.expiresAt < Date.now()) {
    primedCache.delete(keySig);
    return null;
  }
  // Single-use: remove after consumption so a later remount actually fetches.
  primedCache.delete(keySig);
  return entry.data;
}

// ── Module-level invalidation registry ──
// Maps a root-key string → Set<refresh fn>. A "root key" is either the scalar
// key itself (for plain-string keys) or the first element of an array key.
// This lets callers fire-and-forget `invalidate('commitments')` and both
// `useResource('commitments', …)` and `useResource(['commitments', filter], …)`
// receive the signal.
const subscribers = new Map();

function rootOf(key) {
  if (Array.isArray(key)) return String(key[0] ?? '');
  if (typeof key === 'object' && key !== null) return '';
  return String(key);
}

function subscribe(rootKey, refreshFn) {
  if (!rootKey) return () => {};
  if (!subscribers.has(rootKey)) subscribers.set(rootKey, new Set());
  const set = subscribers.get(rootKey);
  set.add(refreshFn);
  return () => {
    set.delete(refreshFn);
    if (set.size === 0) subscribers.delete(rootKey);
  };
}

/**
 * Imperatively re-fetch every live `useResource` whose root-key matches.
 * Safe to call from anywhere — event handlers, other hooks, action creators.
 *
 *   invalidate('commitments');             // re-runs every commitments subscriber
 *   invalidate(['stakeholder-heat']);       // same as invalidate('stakeholder-heat')
 *   invalidate(['tasks', 'activity-timeline']); // invalidate multiple roots
 */
export function invalidate(keyOrKeys) {
  const keys = Array.isArray(keyOrKeys) && keyOrKeys.every((k) => typeof k === 'string')
    ? keyOrKeys
    : [keyOrKeys];
  keys.forEach((k) => {
    const root = rootOf(k);
    const set = subscribers.get(root);
    if (!set) return;
    set.forEach((fn) => { try { fn(); } catch (err) { logger.error('invalidate subscriber failed:', err); } });
  });
}

/**
 * Broadcast a "data has mutated" signal across the app. Every live
 * `useResource` subscriber re-fetches; views that listen to the
 * `polkdesq:data-mutated` window event (e.g. `useTodayData`) also refresh.
 *
 * Use sparingly — prefer targeted `invalidate(key)` calls. This is for
 * actions whose downstream effects are too broad to enumerate (e.g.
 * "user completed a task" affects Today, Analytics, Stakeholder Heat,
 * Activity Timeline, Pendency Radar, …).
 *
 * @param {string} [reason] short description used for diagnostics
 * @param {object} [meta]   optional entity info { type, id }
 */
export function emitDataMutation(reason = 'mutation', meta = {}) {
  // Refresh every live useResource subscriber regardless of key.
  subscribers.forEach((set) => {
    set.forEach((fn) => {
      try { fn(); } catch (err) { logger.error('emitDataMutation subscriber failed:', err); }
    });
  });
  // Also dispatch a window event so non-useResource consumers can listen.
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('polkdesq:data-mutated', { detail: { reason, ...meta } }));
    } catch (err) {
      logger.error('polkdesq:data-mutated dispatch failed:', err);
    }
  }
}

export function useResource(key, fetcher, { enabled = true, initialData = null } = {}) {
  // Consume any primed data on the very first render (lazy init avoids the
  // network round trip entirely when a parent has pre-fetched via a bundle).
  const [data, setData] = useState(() => {
    if (!enabled) return initialData;
    const primed = consumePrime(sigOf(key));
    return primed !== null && primed !== undefined ? primed : initialData;
  });
  // If we hydrated from prime, treat the resource as loaded — no spinner.
  const hydratedFromPrime = useRef(data !== null && data !== initialData);
  const [loading, setLoading] = useState(Boolean(enabled) && !hydratedFromPrime.current);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Stable JSON-encoded key so array/object `key` inputs compare by value.
  const keySig = Array.isArray(key) || typeof key === 'object' ? JSON.stringify(key) : String(key);

  const run = useCallback(async (signal) => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current({ signal });
      if (!mountedRef.current || signal.aborted) return;
      setData(result);
    } catch (err) {
      if (signal.aborted || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      if (!mountedRef.current) return;
      setError(err);
      logger.error('useResource fetch failed:', err);
    } finally {
      if (mountedRef.current && !signal.aborted) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    const controller = new AbortController();
    // Skip the initial network fetch when data was hydrated from the prime
    // cache — the parent already paid that cost in a single super-bundle.
    // Subsequent `invalidate()` / `refresh()` calls still go through `run`,
    // so widgets continue to refresh on data-mutation events as before.
    if (hydratedFromPrime.current) {
      hydratedFromPrime.current = false;
      setLoading(false);
      return () => controller.abort();
    }
    run(controller.signal);
    return () => controller.abort();
  }, [keySig, run]);

  const refresh = useCallback(() => {
    const controller = new AbortController();
    return run(controller.signal);
  }, [run]);

  // Register with the invalidation bus so other components can trigger a re-fetch.
  useEffect(() => {
    let parsed;
    try { parsed = JSON.parse(keySig); } catch { parsed = keySig; }
    const root = rootOf(parsed);
    return subscribe(root, refresh);
  }, [keySig, refresh]);

  return { data, loading, error, refresh, setData };
}
